import { streamingRemoteFeedback, streamingResponseText, streamingFieldText, streamingFeedbackText } from "../i18n/streamingFeedbackPresentation";
import { useStandaloneLanguage, type StandaloneMessage, stLocal, stException, stFeedback, stMessage } from "../i18n/streamingPagesPresentation";
/**
 * useUserTemplates -- React hooks for WSD User Account Template CRUD.
 *
 * Provides hooks for listing, creating, updating, deleting, importing,
 * applying, and duplicating user account templates.
 *
 * Follows the same manual fetch + CSRF pattern used by useUserPolicy and
 * useUserSettings (no react-query dependency).
 *
 * Endpoints:
 * - GET    /api/streaming/templates
 * - GET    /api/streaming/templates/:id
 * - POST   /api/streaming/templates             (CSRF)
 * - PUT    /api/streaming/templates/:id          (CSRF)
 * - DELETE /api/streaming/templates/:id          (CSRF)
 * - POST   /api/streaming/templates/import-from-user  (CSRF)
 * - POST   /api/streaming/templates/:id/apply/:serverType/:userId  (CSRF)
 * - POST   /api/streaming/templates/:id/duplicate  (CSRF)
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { getCsrfToken } from "../lib/csrf";

// ============================================================================
// Types
// ============================================================================

/** Policy fields stored in a template (all optional) */
export interface TemplatePolicyFields {
  IsDisabled?: boolean;
  IsHidden?: boolean;
  IsHiddenRemotely?: boolean;
  IsHiddenFromUnusedDevices?: boolean;
  EnableRemoteAccess?: boolean;
  EnableMediaPlayback?: boolean;
  EnableAudioPlaybackTranscoding?: boolean;
  EnableVideoPlaybackTranscoding?: boolean;
  EnablePlaybackRemuxing?: boolean;
  EnableSyncTranscoding?: boolean;
  EnableMediaConversion?: boolean;
  AutoRemoteQuality?: number;
  SimultaneousStreamLimit?: number;
  RemoteClientBitrateLimit?: number;
  EnableAllFolders?: boolean;
  EnabledFolderNames?: string[];
  EnableAllChannels?: boolean;
  EnableContentDeletion?: boolean;
  EnableContentDownloading?: boolean;
  EnableSubtitleDownloading?: boolean;
  EnableSubtitleManagement?: boolean;
  MaxParentalRating?: number;
  BlockUnratedItems?: string[];
  BlockedTags?: string[];
  IncludeTags?: string[];
  IsTagBlockingModeInclusive?: boolean;
  AllowTagOrRating?: boolean;
  EnablePublicSharing?: boolean;
  AllowSharingPersonalItems?: boolean;
  AllowCameraUpload?: boolean;
  EnableRemoteControlOfOtherUsers?: boolean;
  EnableSharedDeviceControl?: boolean;
  EnableLiveTvAccess?: boolean;
  EnableLiveTvManagement?: boolean;
  RestrictedFeatures?: string[];
  EnableUserPreferenceAccess?: boolean;
  EnableAllDevices?: boolean;
}

/** Configuration/preference fields stored in a template */
export interface TemplateConfigFields {
  AudioLanguagePreference?: string;
  SubtitleLanguagePreference?: string;
  SubtitleMode?: string;
  PlayDefaultAudioTrack?: boolean;
  DisplayMissingEpisodes?: boolean;
  EnableNextEpisodeAutoPlay?: boolean;
  HidePlayedInLatest?: boolean;
  RememberAudioSelections?: boolean;
  RememberSubtitleSelections?: boolean;
  IntroSkipMode?: string;
}

/** WSD-local enforcement fields */
export interface TemplateWsdLocalFields {
  bypassKillStream?: boolean;
}

/** Full template record as returned from the API */
export interface WsdUserTemplate {
  id: number;
  name: string;
  description: string | null;
  descriptionPresentation?: unknown;
  serverType: string | null;
  policyFields: TemplatePolicyFields;
  configFields: TemplateConfigFields;
  wsdLocalFields: TemplateWsdLocalFields;
  isDefault: boolean;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/** Result shape from the apply endpoint */
export interface ApplyTemplateResult {
  applied: boolean;
  policyFieldCount: number;
  configFieldCount: number;
  unresolvedLibraries: string[];
  policyError?: string;
  configError?: string;
}

// ============================================================================
// Helpers
// ============================================================================

const csrfHeaders = (): Record<string, string> => {
  const token = getCsrfToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["x-csrf-token"] = token;
  return headers;
};

// ============================================================================
// Cache
// ============================================================================

interface CachedTemplateList {
  data: WsdUserTemplate[];
  fetchedAt: number;
}

const listCache = new Map<string, CachedTemplateList>();
const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

function buildListCacheKey(serverType?: string): string {
  return `templates:${serverType ?? "all"}`;
}

/** Invalidate all list cache entries */
function invalidateListCache(): void {
  listCache.clear();
}

// ============================================================================
// useTemplates (list)
// ============================================================================

export function useTemplates(
  serverType?: string,
  enabled: boolean = true
): {
  templates: WsdUserTemplate[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [templates, setTemplates] = useState<WsdUserTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorFeedback, setError] = useState<string | StandaloneMessage | null>(null);
  useStandaloneLanguage();
  const error = stMessage(errorFeedback);
  const abortRef = useRef<AbortController | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  const fetchTemplates = useCallback(
    async (force?: boolean) => {
      if (!enabled) return;

      const key = buildListCacheKey(serverType);

      // Check cache unless forced
      if (!force) {
        const cached = listCache.get(key);
        if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
          setTemplates(cached.data);
          setError(null);
          fetchedKeyRef.current = key;
          return;
        }
      }

      if (isFetchingRef.current && fetchedKeyRef.current === key) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const url = serverType
          ? `/api/streaming/templates?serverType=${encodeURIComponent(serverType)}`
          : "/api/streaming/templates";
        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();

        if (!res.ok || !json.ok) {
          throw stException((streamingRemoteFeedback(json) ?? json.message ?? stLocal("failedToLoadTemplates")));
        }

        const fetched = (json.templates ?? []) as WsdUserTemplate[];
        setTemplates(fetched);
        setError(null);

        listCache.set(key, { data: fetched, fetchedAt: Date.now() });
        fetchedKeyRef.current = key;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message = stFeedback(err, "failedToLoadTemplates");
        setError(message);
      } finally {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    },
    [serverType, enabled]
  );

  useEffect(() => {
    if (!enabled) {
      setTemplates([]);
      setError(null);
      fetchedKeyRef.current = null;
      return;
    }

    const key = buildListCacheKey(serverType);
    if (fetchedKeyRef.current !== key) {
      void fetchTemplates();
    }

    return () => {
      abortRef.current?.abort();
    };
  }, [serverType, enabled, fetchTemplates]);

  const refetch = useCallback(() => {
    void fetchTemplates(true);
  }, [fetchTemplates]);

  return { templates, isLoading, error, refetch };
}

// ============================================================================
// useImportTemplateFromUser
// ============================================================================

export function useImportTemplateFromUser(): {
  importing: boolean;
  importFromUser: (
    serverType: string,
    userId: string,
    name: string,
    description?: string
  ) => Promise<WsdUserTemplate | null>;
} {
  const [importing, setImporting] = useState(false);

  const importFromUser = useCallback(
    async (
      serverType: string,
      userId: string,
      name: string,
      description?: string
    ): Promise<WsdUserTemplate | null> => {
      setImporting(true);
      try {
        const body: Record<string, string> = { serverType, userId, name };
        if (description) body.description = description;

        const res = await fetch("/api/streaming/templates/import-from-user", {
          method: "POST",
          headers: csrfHeaders(),
          body: JSON.stringify(body),
        });

        const json = await res.json();
        if (!res.ok || !json.ok) {
          throw stException((streamingRemoteFeedback(json) ?? json.message ?? stLocal("failedToImportTemplateFromUser")));
        }

        invalidateListCache();
        return json.template as WsdUserTemplate;
      } catch (err) {
        const message = stFeedback(err, "failedToImportTemplateFromUser");
        throw stException(message);
      } finally {
        setImporting(false);
      }
    },
    []
  );

  return { importing, importFromUser };
}

// ============================================================================
// useApplyTemplate
// ============================================================================

export function useApplyTemplate(): {
  applying: boolean;
  applyTemplate: (
    templateId: number,
    serverType: string,
    userId: string
  ) => Promise<ApplyTemplateResult | null>;
} {
  const [applying, setApplying] = useState(false);

  const applyTemplate = useCallback(
    async (
      templateId: number,
      serverType: string,
      userId: string
    ): Promise<ApplyTemplateResult | null> => {
      setApplying(true);
      try {
        const url = `/api/streaming/templates/${templateId}/apply/${encodeURIComponent(serverType)}/${encodeURIComponent(userId)}`;

        const res = await fetch(url, {
          method: "POST",
          headers: csrfHeaders(),
          body: JSON.stringify({}),
        });

        const json = await res.json();
        if (!res.ok || !json.ok) {
          throw stException((streamingRemoteFeedback(json) ?? json.message ?? stLocal("failedToApplyTemplate")));
        }

        return json.result as ApplyTemplateResult;
      } catch (err) {
        const message = stFeedback(err, "failedToApplyTemplate");
        throw stException(message);
      } finally {
        setApplying(false);
      }
    },
    []
  );

  return { applying, applyTemplate };
}

// ============================================================================
// useDeleteTemplate
// ============================================================================

export function useDeleteTemplate(): {
  deleting: boolean;
  deleteTemplate: (id: number) => Promise<boolean>;
} {
  const [deleting, setDeleting] = useState(false);

  const deleteTemplateFn = useCallback(async (id: number): Promise<boolean> => {
    setDeleting(true);
    try {
      // DELETE has no body — only send CSRF token, not Content-Type: application/json
      const token = getCsrfToken();
      const headers: Record<string, string> = {};
      if (token) headers["x-csrf-token"] = token;

      const res = await fetch(`/api/streaming/templates/${id}`, {
        method: "DELETE",
        headers,
      });

      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw stException((streamingRemoteFeedback(json) ?? json.message ?? stLocal("failedToDeleteTemplate")));
      }

      invalidateListCache();
      return true;
    } catch (err) {
      const message = stFeedback(err, "failedToDeleteTemplate");
      throw stException(message);
    } finally {
      setDeleting(false);
    }
  }, []);

  return { deleting, deleteTemplate: deleteTemplateFn };
}

// ============================================================================
// useDuplicateTemplate
// ============================================================================

export function useDuplicateTemplate(): {
  duplicating: boolean;
  duplicateTemplate: (id: number, name: string) => Promise<WsdUserTemplate | null>;
} {
  const [duplicating, setDuplicating] = useState(false);

  const duplicateTemplateFn = useCallback(
    async (id: number, name: string): Promise<WsdUserTemplate | null> => {
      setDuplicating(true);
      try {
        const res = await fetch(`/api/streaming/templates/${id}/duplicate`, {
          method: "POST",
          headers: csrfHeaders(),
          body: JSON.stringify({ name }),
        });

        const json = await res.json();
        if (!res.ok || !json.ok) {
          throw stException((streamingRemoteFeedback(json) ?? json.message ?? stLocal("failedToDuplicateTemplate")));
        }

        invalidateListCache();
        return json.template as WsdUserTemplate;
      } catch (err) {
        const message = stFeedback(err, "failedToDuplicateTemplate");
        throw stException(message);
      } finally {
        setDuplicating(false);
      }
    },
    []
  );

  return { duplicating, duplicateTemplate: duplicateTemplateFn };
}

// ============================================================================
// useCreateTemplate
// ============================================================================

/** Payload for creating a new template */
export interface CreateTemplatePayload {
  name: string;
  description?: string;
  serverType?: string | null;
  isDefault?: boolean;
  policyFields?: TemplatePolicyFields;
  configFields?: TemplateConfigFields;
  wsdLocalFields?: TemplateWsdLocalFields;
}

export function useCreateTemplate(): {
  creating: boolean;
  createTemplate: (payload: CreateTemplatePayload) => Promise<WsdUserTemplate | null>;
} {
  const [creating, setCreating] = useState(false);

  const createTemplateFn = useCallback(
    async (payload: CreateTemplatePayload): Promise<WsdUserTemplate | null> => {
      setCreating(true);
      try {
        const res = await fetch("/api/streaming/templates", {
          method: "POST",
          headers: csrfHeaders(),
          body: JSON.stringify(payload),
        });

        const json = await res.json();
        if (!res.ok || !json.ok) {
          throw stException((streamingRemoteFeedback(json) ?? json.message ?? stLocal("failedToCreateTemplate")));
        }

        invalidateListCache();
        return json.template as WsdUserTemplate;
      } catch (err) {
        const message = stFeedback(err, "failedToCreateTemplate");
        throw stException(message);
      } finally {
        setCreating(false);
      }
    },
    []
  );

  return { creating, createTemplate: createTemplateFn };
}

// ============================================================================
// useUpdateTemplate
// ============================================================================

/** Payload for updating an existing template */
export interface UpdateTemplatePayload {
  name?: string;
  description?: string | null;
  serverType?: string | null;
  isDefault?: boolean;
  policyFields?: TemplatePolicyFields;
  configFields?: TemplateConfigFields;
  wsdLocalFields?: TemplateWsdLocalFields;
}

export function useUpdateTemplate(): {
  updating: boolean;
  updateTemplate: (id: number, payload: UpdateTemplatePayload) => Promise<WsdUserTemplate | null>;
} {
  const [updating, setUpdating] = useState(false);

  const updateTemplateFn = useCallback(
    async (id: number, payload: UpdateTemplatePayload): Promise<WsdUserTemplate | null> => {
      setUpdating(true);
      try {
        const res = await fetch(`/api/streaming/templates/${id}`, {
          method: "PUT",
          headers: csrfHeaders(),
          body: JSON.stringify(payload),
        });

        const json = await res.json();
        if (!res.ok || !json.ok) {
          throw stException((streamingRemoteFeedback(json) ?? json.message ?? stLocal("failedToUpdateTemplate")));
        }

        invalidateListCache();
        return json.template as WsdUserTemplate;
      } catch (err) {
        const message = stFeedback(err, "failedToUpdateTemplate");
        throw stException(message);
      } finally {
        setUpdating(false);
      }
    },
    []
  );

  return { updating, updateTemplate: updateTemplateFn };
}
